import React, { useEffect, useState } from "react";
import SlidingPane from "react-sliding-pane";
import MaterialTable, { MTableToolbar } from "material-table";
import DatePicker from "react-datepicker";
import _ from "lodash";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";

import { getTrans as t } from "../../../Localization/translations";
import Helper from "../../../Helper";
import { API } from "../../../backend";
import authHeader from "../../../services/auth-header";

const AuditLogReport = () => {
  // const tableRef = React.createRef();
  const tableRef = React.useRef();

  const [state1, setState] = useState({
    isPaneOpen: false,
  });

  // Filters
  const [model, setModel] = useState("contract"); // default
  const [contractNumber, setContractNumber] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [actor, setActor] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [modelOptions, setModelOptions] = useState([]);

  const handelSearch = () => {
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  };

  const handleClear = () => {
    setActor("");
    setStartDate("");
    setModel("");
    setEndDate("");
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  };

  // put this inside your parent component (alongside other helpers)
  const formatChangeValue = (val) => {
    if (val === null || val === undefined) return "";
    // if it's an object/array, make it readable
    if (typeof val === "object") {
      try {
        return JSON.stringify(val, null, 2); // pretty print
      } catch (e) {
        return String(val);
      }
    }
    return String(val);
  };

  const fetchAllDataForExport = async () => {
    let allData = [];
    let currentPage = 0;
    const pageSize = 10;

    while (true) {
      const query = {
        pageSize,
        page: currentPage,
        search: "",
      };

      let url = API + "helper/audit-log-report/?";
      url += "&per_page=" + query.pageSize;
      url += "&page=" + (query.page + 1);
      url += "&search=" + query.search;
      url += `&model=${model || ""}`;
      url += `&contractNumber=${contractNumber || ""}`;
      url += `&invoiceNumber=${invoiceNumber || ""}`;
      url += `&actor=${actor || ""}`;
      url += `&start_date=${startDate ? Helper.DatePicker(startDate) : ""}`;
      url += `&end_date=${endDate ? Helper.DatePicker(endDate) : ""}`;

      try {
        const response = await fetch(url, { headers: authHeader() });
        const result = await response.json();

        if (!result.data || result.data.length === 0) {
          break;
        }

        allData = allData.concat(result.data);
        currentPage++;
        if (result.total && allData.length >= result.total) {
          break;
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        break;
      }
    }
    // Normalize change rows from possible backend keys and add 'changes_text' for export
    allData = allData.map((r) => {
      const changeRows =
        r.change_rows || r.changes || r.changeRows || r.changes_rows || r.changesRows || [];
      return {
        ...r,
        // keep original key too, but ensure changes_text exists
        changes_text: serializeChanges(changeRows),
      };
    });

    return allData;
  };


  useEffect(() => {
    fetch(API + "helper/audit-log-models/", {
      headers: authHeader(),
    })
      .then((res) => res.json())
      .then((result) => {
        setModelOptions(result.data || []);
      })
      .catch((err) => {
        console.error("Failed to load models", err);
      });
  }, []);

  // produce a readable single-line summary for one change row
  const serializeChangeRow = (c) => {
    const from = typeof c.from === "object" ? JSON.stringify(c.from) : String(c.from ?? "");
    const to = typeof c.to === "object" ? JSON.stringify(c.to) : String(c.to ?? "");
    // short format: field: old -> new
    return `${c.field}: ${from} -> ${to}`;
  };

  // join all change rows into one text; used for exports
  const serializeChanges = (changeRows) => {
    if (!changeRows || changeRows.length === 0) return "";
    // join with " | " to keep CSV/PDF readable
    return changeRows.map((c) => serializeChangeRow(c)).join(" | ");
  };

  // helper to safely CSV-escape a value (wrap in quotes and double any quotes)
  const csvSafe = (val) => {
    if (val === null || val === undefined) return '""';
    const s = String(val);
    return `"${s.replace(/"/g, '""')}"`;
  };

  return (
    <div className="container-fluid">
      <div className="">
        <div className="row show_contract show_invoice">
          <div className="col-md-12">
            <div className="mb-0">
              <div className="body">
                <div className="table-responsive p-0">
                  <MaterialTable
                    tableRef={tableRef}
                    style={{
                      backgroundColor: "var(--bs-table-bg)",
                      borderBottomWidth: "1px",
                      boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                    }}
                    title={<h5>{t("Audit Log Report")}</h5>}
                    columns={[
                      {
                        title: t("Timestamp"),
                        field: "timestamp",
                        render: (rowData) => (
                          <>
                            <div className="d-xl-none tc d-md-none d-lg-none">
                              {t("Timestamp")}
                            </div>
                            <div>
                              {rowData.timestamp
                                ? Helper.DateTimeNormal(rowData.timestamp)
                                : ""}
                            </div>
                          </>
                        ),
                        headerStyle: {
                          verticalAlign: "inherit",
                          fontWeight: "bold",
                          textAlign: "left",
                        },
                        cellStyle: { textAlign: "left", fontSize: 12 },
                      },
                      {
                        title: t("User"),
                        field: "actor",
                        render: (rowData) => (
                          <>
                            <div className="d-xl-none tc d-md-none d-lg-none">
                              {t("User")}
                            </div>
                            <div>{rowData.actor || ""}</div>
                          </>
                        ),
                        headerStyle: {
                          verticalAlign: "inherit",
                          fontWeight: "bold",
                          textAlign: "left",
                        },
                        cellStyle: { textAlign: "left", fontSize: 12 },
                      },
                      {
                        title: t("Object"),
                        field: "model",
                        render: (rowData) => (
                          <>
                            <div className="d-xl-none tc d-md-none d-lg-none">
                              {t("Object")}
                            </div>
                            <div>{rowData.model}</div>
                          </>
                        ),
                        headerStyle: {
                          verticalAlign: "inherit",
                          fontWeight: "bold",
                          textAlign: "left",
                        },
                        cellStyle: { textAlign: "left", fontSize: 12 },
                      },
                      {
                        title: t("Action"),
                        field: "action",
                        render: (rowData) => (
                          <>
                            <div className="d-xl-none tc d-md-none d-lg-none">
                              {t("Action")}
                            </div>
                            <div>{rowData.action}</div>
                          </>
                        ),
                        headerStyle: {
                          verticalAlign: "inherit",
                          fontWeight: "bold",
                          textAlign: "left",
                        },
                        cellStyle: { textAlign: "left", fontSize: 12 },
                      },

                      {
                        title: t("Transaction"),
                        field: "object_repr",
                        render: (rowData) => (
                          <>
                            <div className="d-xl-none tc d-md-none d-lg-none">
                              {t("Transaction")}
                            </div>
                            <div>{rowData.object_repr}</div>
                          </>
                        ),
                        headerStyle: {
                          verticalAlign: "inherit",
                          fontWeight: "bold",
                          textAlign: "left",
                        },
                        cellStyle: { textAlign: "left", fontSize: 12 },
                      },
                    ]}
                    detailPanel={(rowData) => (
                      <div style={{ padding: "16px" }}>
                        {/* <h6>{t("Changes")}</h6>
      {(!rowData.change_rows || rowData.change_rows.length === 0) && (
        <div>{t("No changes recorded")}</div>
      )}
      {rowData.change_rows && rowData.change_rows.length > 0 && (
        <table className="table table-sm">
          <thead>
            <tr>
              <th>{t("Field")}</th>
              <th>{t("From")}</th>
              <th>{t("To")}</th>
            </tr>
          </thead>
          <tbody>
            {rowData.change_rows.map((c, idx) => (
              <tr key={idx}>
                <td>{c.field}</td>
                <td>{c.from}</td>
                <td>{c.to}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )} */}
                        <MaterialTable
                          title="Changes"
                          style={{
                            padding: "0.5rem 0.5rem",
                            borderBottomWidth: "1px",
                            boxShadow:
                              "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                            width: "100%",
                          }}
                          columns={[
                            {
                              title: "Field",
                              field: "field",
                              render: (changeRow) => (
                                <>
                                  <div className="d-xl-none tc d-md-none d-lg-none">
                                    {t("Field")}
                                  </div>
                                  <div className="d-sm-block d-md-block d-lg-block d-xl-block">
                                    {changeRow.field}
                                  </div>
                                </>
                              ),
                              headerStyle: {
                                verticalAlign: "inherit",
                                fontWeight: "bold",
                                textAlign: "left",
                              },
                              cellStyle: {
                                textAlign: "left",
                                fontSize: 12,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                              },
                            },
                            {
                              title: "From",
                              field: "from",
                              render: (changeRow) => (
                                <>
                                  <div className="d-xl-none tc d-md-none d-lg-none">
                                    {t("From")}
                                  </div>
                                  <div
                                    className="d-sm-block d-md-block d-lg-block d-xl-block"
                                    style={{
                                      whiteSpace: "pre-wrap",
                                      fontFamily: "monospace",
                                      fontSize: 12,
                                    }}
                                  >
                                    {formatChangeValue(changeRow.from)}
                                  </div>
                                </>
                              ),
                              headerStyle: {
                                verticalAlign: "inherit",
                                fontWeight: "bold",
                                textAlign: "left",
                              },
                              cellStyle: {
                                textAlign: "left",
                                fontSize: 12,
                              },
                            },
                            {
                              title: "To",
                              field: "to",
                              render: (changeRow) => (
                                <>
                                  <div className="d-xl-none tc d-md-none d-lg-none">
                                    {t("To")}
                                  </div>
                                  <div
                                    className="d-sm-block d-md-block d-lg-block d-xl-block"
                                    style={{
                                      whiteSpace: "pre-wrap",
                                      fontFamily: "monospace",
                                      fontSize: 12,
                                    }}
                                  >
                                    {formatChangeValue(changeRow.to)}
                                  </div>
                                </>
                              ),
                              headerStyle: {
                                verticalAlign: "inherit",
                                fontWeight: "bold",
                                textAlign: "left",
                              },
                              cellStyle: {
                                textAlign: "left",
                                fontSize: 12,
                              },
                            },
                          ]}
                          data={rowData.change_rows || []}
                          options={{
                            search: false,
                            paging: false,
                            sorting: false,
                            toolbar: false,
                            headerStyle: { fontWeight: "bold" },
                            maxBodyHeight: "300px",
                            draggable: false,
                          }}
                        />
                      </div>
                    )}
                    data={(query) =>
                      new Promise((resolve, reject) => {
                        if (!model) {
                          resolve({ data: [], page: 0, totalCount: 0 });
                          return;
                        }
                        let url = API + "helper/audit-log-report/?";
                        url += "&per_page=" + query.pageSize;
                        url += "&page=" + (query.page + 1);
                        url += "&search=" + query.search; // you can ignore search in backend if you like
                        url += `&actor=${actor || ""}`;
                        url += `&model=${model || ""}`;
                        url += `&start_date=${startDate ? Helper.DatePicker(startDate) : ""
                          }`;
                        url += `&end_date=${endDate ? Helper.DatePicker(endDate) : ""
                          }`;

                        // (Optional) if you later support orderBy/orderDirection in backend:
                        // if (orderBy) {
                        //   url += `&orderBy=${orderBy.field}`;
                        //   url += `&orderDirection=${orderDirection}`;
                        // }

                        fetch(url, { headers: authHeader() })
                          .then((response) => response.json())
                          .then((result) => {
                            const rows = result.data || [];
                            resolve({
                              data:
                                query.orderBy && query.orderDirection
                                  ? _.orderBy(
                                    rows,
                                    [query.orderBy.field],
                                    [query.orderDirection]
                                  )
                                  : rows,
                              page: (result.page || 1) - 1,
                              totalCount: result.total || 0,
                            });
                          })
                          .catch((err) => reject(err));
                      })
                    }
                    components={{
                      Toolbar: (props) => (
                        <div className="MuiToolbar-root MTableToolbar-spacer-21  my-temp-class">
                          <MTableToolbar {...props} />
                          <span
                            onClick={() => setState({ isPaneOpen: true })}
                            style={{
                              color: "#b4b7c5",
                              paddingRight: "1px",
                              cursor: "pointer",
                            }}
                          >
                            <img
                              src={
                                process.env.PUBLIC_URL +
                                "/static/images/ic-filter.svg"
                              }
                              alt="filter"
                            />
                          </span>
                        </div>
                      ),
                    }}
                    options={{
                      headerStyle: {
                        backgroundColor: "white",
                        color: "#245C7F",
                        fontSize: 12,
                      },
                      rowStyle: (rowData, index) =>
                        index % 2 ? { backgroundColor: "#fff" } : {},
                      sorting: true,
                      search: true,
                      toolbar: true,
                      paging: true,
                      pageSize: 10,
                      emptyRowsWhenPaging: false,
                      pageSizeOptions: [10, 25, 50, 100],
                      paginationType: "stepped",
                      showFirstLastPageButtons: false,
                      exportAllData: true,
                      exportButton: true,
                      exportFileName: "AuditLogReport",
                      actionsColumnIndex: -1,
                      columnsButton: true,
                      exportCsv: async (columns, data) => {
                        try {
                          const allData = await fetchAllDataForExport();
                          if (allData.length === 0) {
                            console.error("No data found for CSV export");
                            return;
                          }

                          // visible columns + append Changes column (field: changes_text)
                          const csvColumns = Helper.visibleColumn(columns).concat([{ title: t("Changes"), field: "changes_text" }]);
                          const csvColumnTitles = csvColumns.map((column) => column.title);

                          const title = "Audit Log Report";
                          const currentDate = new Date().toLocaleDateString();

                          let csvContentWithInfo = `${title}\n`;
                          csvContentWithInfo += `Model: ${model}\n`;
                          if (contractNumber) {
                            csvContentWithInfo += `Contract: ${contractNumber}\n`;
                          }
                          if (invoiceNumber) {
                            csvContentWithInfo += `Invoice: ${invoiceNumber}\n`;
                          }
                          if (actor) {
                            csvContentWithInfo += `Actor: ${actor}\n`;
                          }
                          csvContentWithInfo += `Date: ${currentDate}\n\n`;

                          const csvData = allData.map((rowData) =>
                            csvColumns.map((columnDef) => {
                              const val = rowData[columnDef.field];
                              return csvSafe(val);
                            })
                          );

                          const csvRows = [csvColumnTitles, ...csvData];
                          const csvContent = `${csvContentWithInfo}${csvRows.map((row) => row.join(",")).join("\n")}`;

                          const blob = new Blob([csvContent], {
                            type: "text/csv;charset=utf-8;",
                          });
                          const link = document.createElement("a");
                          link.href = URL.createObjectURL(blob);
                          link.download = "AuditLogReport.csv";
                          link.style.display = "none";
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        } catch (error) {
                          console.error("Error exporting CSV:", error);
                        }
                      },
                      exportPdf: async (columns, data) => {
                        const allData = await fetchAllDataForExport();
                        if (allData.length === 0) {
                          console.error("No data found for PDF export");
                          return;
                        }

                        const visibleColumns = Helper.visibleColumn(columns).concat([{ title: t("Changes"), field: "changes_text" }]);
                        const doc = new jsPDF();
                        doc.setFont("helvetica", "bold");
                        doc.setFontSize(16);

                        const title = "Audit Log Report";
                        doc.text(title, 14, 10);

                        doc.setFontSize(12);
                        doc.setFont("helvetica", "normal");
                        const currentDate = new Date().toLocaleDateString();
                        doc.text(`Date : ${currentDate}`, 14, 15);
                        doc.text(`Model : ${model}`, 14, 20);

                        let y = 25;
                        if (contractNumber) {
                          doc.text(`Filter Contract : ${contractNumber}`, 14, y);
                          y += 5;
                        }
                        if (invoiceNumber) {
                          doc.text(`Filter Invoice : ${invoiceNumber}`, 14, y);
                          y += 5;
                        }
                        if (actor) {
                          doc.text(`Filter Actor : ${actor}`, 14, y);
                          y += 5;
                        }

                        const tableStartY = y + 3;

                        const columnTitles = visibleColumns.map((c) => c.title);
                        const pdfData = allData.map((rowData) =>
                          visibleColumns.map((columnDef) => {
                            const v = rowData[columnDef.field];
                            return v === undefined || v === null ? "" : String(v);
                          })
                        );

                        autoTable(doc, {
                          head: [columnTitles],
                          body: pdfData,
                          startY: tableStartY,
                          styles: { fontSize: 9 },
                          headStyles: { fillColor: [41, 128, 185] },
                        });

                        doc.save(`AuditLogReport.pdf`);
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SlidingPane
        className="some-custom-class"
        overlayClassName="some-custom-overlay-class"
        isOpen={state1.isPaneOpen}
        title="Filter By"
        subtitle=""
        onRequestClose={() => {
          setState({ isPaneOpen: false });
        }}
      >
        <label className="col-lg-12 col-form-label">{t("User")}</label>
        <div className="">
          <input
            className="form-control"
            placeholder={t("Actor")}
            value={actor}
            onChange={(e) => setActor(e.target.value)}
          />
        </div>

        <label className="col-lg-12 col-form-label">{t("Model")}</label>
        <select
          className="form-control"
          value={model}
          onChange={(e) => setModel(e.target.value)}
        >
          <option value="">All</option>

          {modelOptions.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>

        <label className="col-lg-12 col-form-label">{t("Start Date")}</label>
        <div className="">
          <DatePicker
            className="form-control datepick"
            selected={startDate}
            onChange={(e) => setStartDate(e)}
            placeholderText={"mm/dd/yyyy"}
            showYearDropdown
            scrollableYearDropdown
            showMonthDropdown
            dropdownMode="select"
          />
        </div>

        <label className="col-lg-12 col-form-label">{t("End Date")}</label>
        <div className="">
          <DatePicker
            className="form-control datepick"
            selected={endDate}
            onChange={(e) => setEndDate(e)}
            placeholderText={"mm/dd/yyyy"}
            showYearDropdown
            scrollableYearDropdown
            showMonthDropdown
            dropdownMode="select"
          />
        </div>

        <div className="col-xl-12">
          <div className="form-group row">
            <div className="col-lg-12 text-left" style={{ marginTop: "30px" }}>
              <button
                type="button"
                className="clearReceipt btn bg-gradient-secondary btn-m ms-auto mb-0"
                onClick={handleClear}
              >
                {t("Reset")}
              </button>
              &nbsp;&nbsp;
              <button
                type="button"
                className="filter add btn btn-primary btn-m ms-auto mb-0"
                onClick={handelSearch}
              >
                {t("Apply")}
              </button>
              &nbsp;&nbsp;
            </div>
          </div>
        </div>
      </SlidingPane>
    </div>
  );
};

export default AuditLogReport;
